import { Dispatch, SetStateAction, useCallback, useMemo } from "react"
import {
  Block,
  Divider,
  GasFee,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectUpdateLoanAmount,
  selectUpdateStrategy,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import {
  useBorrowingFee,
  useBorrowInterestRate,
  useChangeInLiquidationPriceOnDebtChange,
  useCollateralPrice,
  useDebtAmountWithoutFees,
  useUpdateCollateralizationRatio,
  useUpdatedCollateralBasedOnRatio,
} from "#/shared/hooks"
import { usePosthogBorrowMoreModalEvents } from "#/shared/hooks/posthog-events"
import { BorrowMoreStrategy } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  floatToBigInt,
  getCurrencySymbol,
  separateThousandsWithComma,
} from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import {
  BorrowProgress,
  BorrowReviewBox,
  BorrowReviewListItem,
  BorrowTerms,
} from "../components"

type UpdateReviewProps = ComponentStepProps & {
  handleBorrowMore: () => Promise<void>
  setHasAcceptedTerms: Dispatch<SetStateAction<boolean>>
  hasAcceptedTerms: boolean
}

export default function UpdateReview({
  onClickContinue,
  handleBorrowMore,
  hasAcceptedTerms,
  setHasAcceptedTerms,
}: UpdateReviewProps) {
  const [css, theme] = useStyletron()

  const {
    emitBorrowMoreConfirmIncreaseCollEvent,
    emitBorrowMoreConfirmReduceRatioEvent,
  } = usePosthogBorrowMoreModalEvents()

  const strategy = useDappSelector(selectUpdateStrategy)

  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)

  const { debtAmountWithoutGasComp, debtAmountWithoutGasCompLabel } =
    useDebtAmountWithoutFees()

  const {
    additionalCollateralAmount,
    additionalCollateralAmountLabel,
    updatedCollateralAmountLabel,
    previousCollateralAmountLabel,
  } = useUpdatedCollateralBasedOnRatio(floatToBigInt(updateLoanAmount || "0"))

  const updateLoanAmountLabel = useMemo(
    () => separateThousandsWithComma(updateLoanAmount),
    [updateLoanAmount],
  )

  const { calculateBorrowingFee, borrowingFeeRateLabel } = useBorrowingFee()

  const borrowingFeeAmount = useMemo(
    () => calculateBorrowingFee(floatToBigInt(updateLoanAmount || "0")),
    [updateLoanAmount, calculateBorrowingFee],
  )

  const borrowingFeeAmountLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(borrowingFeeAmount),
        2,
      ),
    [borrowingFeeAmount],
  )

  const { interestRateLabel } = useBorrowInterestRate()

  const onSubmit = useCallback(() => {
    if (strategy === BorrowMoreStrategy.INCREASE_COLLATERAL) {
      emitBorrowMoreConfirmIncreaseCollEvent()
    } else {
      emitBorrowMoreConfirmReduceRatioEvent()
    }
    handleBorrowMore()
    onClickContinue()
  }, [
    emitBorrowMoreConfirmIncreaseCollEvent,
    emitBorrowMoreConfirmReduceRatioEvent,
    handleBorrowMore,
    onClickContinue,
    strategy,
  ])

  const {
    prevLoanWithFeesValue,
    newLoanWithFeesValue,
    prevCollateralizationRatioLabel,
    newCollateralizationRatioLabel,
    collateralizationRatioChangeLabel,
  } = useUpdateCollateralizationRatio()

  const newTotalBorrowAmount = useMemo(() => {
    const newAmount =
      debtAmountWithoutGasComp +
      floatToBigInt(updateLoanAmount) +
      borrowingFeeAmount

    return separateThousandsWithComma(bigIntToHumanReadableFormat(newAmount), 2)
  }, [borrowingFeeAmount, debtAmountWithoutGasComp, updateLoanAmount])

  const {
    newLiquidationPriceLabel,
    changeInLiquidationPriceLabelWithUSD,
    prevLiquidationPriceLabel,
  } = useChangeInLiquidationPriceOnDebtChange(
    prevLoanWithFeesValue,
    newLoanWithFeesValue,
  )

  const { collateralPriceLabelWithUSD } = useCollateralPrice(
    additionalCollateralAmount,
  )

  return (
    <>
      <BorrowProgress step={2} stepsCount={4} />
      <Block
        className={css({
          position: "relative",
          padding: theme.sizing.scale800,
          paddingTop: 0,
          paddingBottom: "170px",
          overflow: "hidden",
        })}
      >
        <Block
          className={css({
            flexDirection: "column",
            display: "flex",
            gap: theme.sizing.scale800,
            height: "100%",
            paddingBottom: theme.sizing.scale800,
            overflow: "scroll",
            scrollbarWidth: "none",
          })}
        >
          {strategy === BorrowMoreStrategy.INCREASE_COLLATERAL && (
            <Block
              className={css({
                borderRadius: theme.borders.radius400,
                border: "1px solid",
                borderColor: theme.colors.borderOpaque,
              })}
            >
              <BorrowReviewListItem
                header="Collateral to be deposited"
                endHeader={`${additionalCollateralAmountLabel} ${getCurrencySymbol(
                  "mBTC",
                )}`}
                endDescription={collateralPriceLabelWithUSD}
              />
              <Divider $size="cell" />
              <Block
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  padding: theme.sizing.scale600,
                  paddingTop: theme.sizing.scale300,
                })}
              >
                <ParagraphSmall color={theme.colors.contentSecondary}>
                  Transaction gas fee
                </ParagraphSmall>
                <Block display="flex" alignItems="center">
                  <GasFee
                    color={theme.colors.contentTertiary}
                    size="14px"
                    className={css({
                      marginBottom: "1px",
                    })}
                  />
                  <ParagraphSmall marginLeft={theme.sizing.scale100}>
                    {"<0.000001"} <CurrencySymbol token="mBTC" />
                  </ParagraphSmall>
                </Block>
              </Block>
            </Block>
          )}
          <BorrowReviewBox>
            <BorrowReviewListItem
              header="Interest Rate (APR)"
              endHeader={`${interestRateLabel}%`}
              description="Fixed, as a percentage of debt"
              margin="0"
            />
            <ParagraphSmall
              margin={`-${theme.sizing.scale500} ${theme.sizing.scale600} ${theme.sizing.scale500}`}
              color={theme.colors.contentTertiary}
            >
              Paid annually
            </ParagraphSmall>
          </BorrowReviewBox>
          <BorrowReviewBox>
            <BorrowReviewListItem
              header="Total collateral"
              endHeader={`${
                strategy === BorrowMoreStrategy.INCREASE_COLLATERAL
                  ? updatedCollateralAmountLabel
                  : previousCollateralAmountLabel
              } ${getCurrencySymbol("mBTC")}`}
              description={
                strategy === BorrowMoreStrategy.INCREASE_COLLATERAL
                  ? `+${additionalCollateralAmountLabel} ${getCurrencySymbol(
                      "mBTC",
                    )}`
                  : undefined
              }
              endDescription={
                strategy === BorrowMoreStrategy.INCREASE_COLLATERAL
                  ? `from ${previousCollateralAmountLabel} ${getCurrencySymbol(
                      "mBTC",
                    )}`
                  : undefined
              }
              hasBorder
            />
            <BorrowReviewListItem
              header="Collateralization ratio"
              endHeader={`${
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? newCollateralizationRatioLabel
                  : prevCollateralizationRatioLabel
              }%`}
              description={
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? `${collateralizationRatioChangeLabel}%`
                  : undefined
              }
              endDescription={
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? `from ${prevCollateralizationRatioLabel}%`
                  : undefined
              }
            />
          </BorrowReviewBox>
          <BorrowReviewBox>
            <BorrowReviewListItem
              header="Liquidation price"
              endHeader={
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? `$${newLiquidationPriceLabel}`
                  : `$${prevLiquidationPriceLabel}`
              }
              description={
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? `${changeInLiquidationPriceLabelWithUSD}`
                  : undefined
              }
              endDescription={
                strategy === BorrowMoreStrategy.DECREASE_RATIO
                  ? `from $${prevLiquidationPriceLabel}`
                  : undefined
              }
            />
          </BorrowReviewBox>
          <BorrowReviewBox backgroundColor={theme.colors.green50}>
            <BorrowReviewListItem
              hasLightHeader
              header="Existing debt"
              endHeader={`${debtAmountWithoutGasCompLabel} MUSD`}
              hasBorder
            />
            <BorrowReviewListItem
              hasLightHeader
              header={`Issuance fee (${borrowingFeeRateLabel}%)`}
              endHeader={`${borrowingFeeAmountLabel} MUSD`}
            />
            <BorrowReviewListItem
              header="New borrowed amount"
              endHeader={`${updateLoanAmountLabel} MUSD`}
              margin={`${theme.sizing.scale800} 0 0`}
              hasBorder
            />
            <BorrowReviewListItem
              header="Total borrow balance"
              endHeader={`${newTotalBorrowAmount} MUSD`}
            />
          </BorrowReviewBox>
        </Block>

        <BorrowTerms
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
          onSubmit={onSubmit}
          label={`Borrow ${updateLoanAmountLabel} MUSD`}
        />
      </Block>
    </>
  )
}
